import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { Icon, Button, Breadcrumb, Switch } from "../../ui";
import { PluginModal } from "../../ui/Business";
import {
  DriverCompanySettings,
  DriverGroupAssignmentSettings,
} from "../../ui/Delivery";
import { BusinessesTab } from "../../ui/Brands";
import { useBusinesses } from "@finitless/shared/hooks";
import { useApiStore } from "@finitless/shared/stores";
import {
  useDriverGroupDetail,
  getSetupModalTitle,
  buildPluginSetupUrl,
  useToggleDriverCompany,
  useUpdateDriverGroup,
} from "../../../hooks/useDriverCompanies";
import "./delivery-services-ordering.css";

const DeliveryServiceDetailPage = ({ selectedBrand }) => {
  const { t } = useTranslation("delivery");
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useApiStore((state) => state.project);

  const {
    plugin: pluginFromState,
    isDriverCompanyId,
    driverCompany: driverCompanyFromState,
  } = location.state || {};

  // Determine if this is a driverCompanyId (no driverGroupId) or a driverGroupId
  const isDriverCompanyMode = isDriverCompanyId === true;
  const driverGroupId = isDriverCompanyMode ? null : parseInt(id);

  // Only fetch driver group detail if we have a valid driverGroupId
  const { data, isLoading, error } = useDriverGroupDetail(driverGroupId);
  const [isPluginModalOpen, setIsPluginModalOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // Businesses tab state
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState(new Set());

  const toggleMutation = useToggleDriverCompany();
  const updateDriverGroupMutation = useUpdateDriverGroup();

  // Use driverCompany from state if in driverCompany mode, otherwise from API data
  const driverGroup = isDriverCompanyMode ? null : data?.driverGroup || {};
  const driverCompany = isDriverCompanyMode
    ? driverCompanyFromState || {}
    : data?.driverCompany || {};

  useEffect(() => {
    if (driverCompany?.enabled !== undefined) {
      setIsEnabled(driverCompany.enabled);
    }
  }, [driverCompany?.enabled]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch businesses
  const { data: businessesData, isLoading: isLoadingBusinesses } =
    useBusinesses({
      enabledFilter: "all",
      searchTerm,
      page: currentPage,
      pageSize: 10,
    });

  const businesses = businessesData?.businesses || [];
  const pagination = businessesData?.pagination || {};

  // Helper function to extract business IDs from driverGroup.business
  const extractBusinessIds = (businessData) => {
    if (!businessData) return [];

    if (Array.isArray(businessData)) {
      // Check if it's an array of objects (with id property) or array of IDs
      if (
        businessData.length > 0 &&
        typeof businessData[0] === "object" &&
        businessData[0].id
      ) {
        // Array of business objects - extract IDs
        return businessData.map((b) => b.id);
      } else {
        // Array of IDs (numbers)
        return businessData;
      }
    } else if (typeof businessData === "string") {
      try {
        const parsed = JSON.parse(businessData || "[]");
        // Check if parsed is array of objects or IDs
        if (
          Array.isArray(parsed) &&
          parsed.length > 0 &&
          typeof parsed[0] === "object" &&
          parsed[0].id
        ) {
          return parsed.map((b) => b.id);
        } else {
          return parsed;
        }
      } catch {
        return [];
      }
    }
    return [];
  };

  // Initialize selected businesses from driverGroup (only if we have a driverGroup)
  useEffect(() => {
    if (!isDriverCompanyMode && driverGroup?.business !== undefined) {
      const businessIds = extractBusinessIds(driverGroup.business);
      setSelectedBusinessIds(new Set(businessIds));
    }
  }, [driverGroup?.business, isDriverCompanyMode]);

  const plugin = pluginFromState || driverCompany?.plugin || null;
  const externalId = driverCompany?.external_id || "";
  const pluginRoot = driverCompany?.plugin_root || "";

  const pluginKey = plugin?.key || externalId || "";
  const setupModalTitle = getSetupModalTitle(pluginKey);

  const pluginSetupUrl = plugin
    ? buildPluginSetupUrl(plugin, project)
    : pluginRoot && plugin?.links?.info_setup
      ? `${pluginRoot}${plugin.links.info_setup.replace(
          "@{project_code}",
          project,
        )}`
      : null;

  const hasSetup = !!pluginSetupUrl;

  // Auto-open setup modal when entering the detail page
  useEffect(() => {
    if (hasSetup) {
      setIsPluginModalOpen(true);
    }
  }, [hasSetup]);

  const breadcrumbItems = [
    {
      label: t("serviceDetail.breadcrumb.deliveryServices"),
      path: "/integrations/delivery-services",
      icon: "ShieldCheck",
    },
    {
      label: driverCompany?.name || t("serviceDetail.fallbacks.detail"),
      path: null,
    },
  ];

  const handleOpenSetupModal = () => {
    setIsPluginModalOpen(true);
  };

  const handleToggle = async (checked) => {
    setIsEnabled(checked);
    try {
      await toggleMutation.mutateAsync({
        driverId: driverCompany?.id,
        enabled: checked,
      });
    } catch {
      setIsEnabled(!checked);
    }
  };

  // Businesses tab handlers
  const handleBusinessToggle = async (businessId) => {
    const isSelected = selectedBusinessIds.has(businessId);
    const previousSelectedIds = new Set(selectedBusinessIds);
    const newSelectedIds = new Set(selectedBusinessIds);

    if (isSelected) {
      newSelectedIds.delete(businessId);
    } else {
      newSelectedIds.add(businessId);
    }

    // Update local state optimistically
    setSelectedBusinessIds(newSelectedIds);

    // Convert Set to array and send as JSON string
    const businessArray = Array.from(newSelectedIds);

    try {
      await updateDriverGroupMutation.mutateAsync({
        driverGroupId,
        data: {
          business: JSON.stringify(businessArray),
        },
      });
    } catch (error) {
      // Revert on error
      setSelectedBusinessIds(previousSelectedIds);
      console.error("Error updating driver group businesses:", error);
    }
  };

  const handleSelectAll = async () => {
    const allBusinessIds = new Set(businesses.map((b) => b.id));
    const alreadyAllSelected =
      allBusinessIds.size > 0 &&
      Array.from(allBusinessIds).every((id) => selectedBusinessIds.has(id));

    if (alreadyAllSelected) {
      return;
    }

    setSelectedBusinessIds(allBusinessIds);
    const businessArray = Array.from(allBusinessIds);

    try {
      await updateDriverGroupMutation.mutateAsync({
        driverGroupId,
        data: {
          business: JSON.stringify(businessArray),
        },
      });
    } catch (error) {
      // Revert on error
      if (driverGroup?.business) {
        const businessIds = extractBusinessIds(driverGroup.business);
        setSelectedBusinessIds(new Set(businessIds));
      }
      console.error("Error updating driver group businesses:", error);
    }
  };

  const handleSelectNone = async () => {
    const alreadyNoneSelected = selectedBusinessIds.size === 0;

    if (alreadyNoneSelected) {
      return;
    }

    setSelectedBusinessIds(new Set());

    try {
      await updateDriverGroupMutation.mutateAsync({
        driverGroupId,
        data: {
          business: [],
        },
      });
    } catch (error) {
      // Revert on error
      if (driverGroup?.business) {
        const businessIds = extractBusinessIds(driverGroup.business);
        setSelectedBusinessIds(new Set(businessIds));
      }
      console.error("Error updating driver group businesses:", error);
    }
  };

  // Callback to update businesses when selecting all
  const handleUpdateBusinesses = async (businessArray) => {
    await updateDriverGroupMutation.mutateAsync({
      driverGroupId,
      data: {
        business: JSON.stringify(businessArray),
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearSearch = () => {
    setSearchInput("");
  };

  // Only show loading state if we're actually loading (not in driverCompany mode)
  if (!isDriverCompanyMode && isLoading) {
    return (
      <div className="ordering-delivery-service-detail">
        <div className="mb-6 animate-pulse">
          <div className="bg-card mb-4 h-4 w-48 rounded"></div>
          <div className="bg-card mb-2 h-8 w-64 rounded"></div>
          <div className="bg-card h-4 w-96 rounded"></div>
        </div>
        <div className="ordering-delivery-service-panel border-border bg-background animate-pulse rounded-lg border p-6">
          <div className="bg-card mb-6 h-10 w-48 rounded"></div>
          <div className="space-y-4">
            <div className="bg-card h-6 w-full rounded"></div>
            <div className="bg-card h-6 w-3/4 rounded"></div>
            <div className="bg-card h-6 w-1/2 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Only show error state if we're in driverGroup mode and there's an error
  if (!isDriverCompanyMode && error) {
    return (
      <div className="ordering-delivery-service-detail">
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader
          title={t("serviceDetail.errorState.pageTitle")}
          subtitle={t("serviceDetail.errorState.pageSubtitle")}
          selectedBrand={selectedBrand}
        />
        <div className="ordering-delivery-service-state border-destructive/30 bg-destructive/20 rounded-lg border p-6">
          <div className="flex items-center space-x-3">
            <div className="text-destructive">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-destructive text-lg font-medium">
                {t("serviceDetail.errorState.title")}
              </h3>
              <p className="text-destructive mt-1 text-sm">
                {error.message || t("serviceDetail.errorState.description")}
              </p>
              <Button
                emphasis="solid"
                intent="neutral"
                size="touch"
                onClick={() => navigate("/integrations/delivery-services")}
                className="mt-4 px-4 py-2 transition-colors"
              >
                {t("serviceDetail.actions.backToServices")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ordering-delivery-service-detail">
      <Breadcrumb items={breadcrumbItems} />

      <PageHeader
        title={driverCompany?.name || t("serviceDetail.header.fallbackTitle")}
        subtitle={t("serviceDetail.header.subtitle")}
        selectedBrand={selectedBrand}
      />

      {/* Driver Company Info Card */}
      <div className="ordering-delivery-service-panel border-border bg-background mb-6 rounded-lg border p-6">
        <div className="flex items-center space-x-4">
          {driverCompany?.logo ? (
            <div className="border-border bg-card flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border p-2">
              <img
                src={driverCompany.logo}
                alt={driverCompany.name}
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="bg-secondary flex hidden h-full w-full items-center justify-center text-2xl font-semibold text-white">
                {driverCompany?.name?.charAt(0)}
              </div>
            </div>
          ) : (
            <div className="border-border bg-card flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg border">
              <Icon name="Truck" className="text-muted-foreground h-10 w-10" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            {/* Name and Toggle in same line */}
            <div className="mb-2 flex items-center justify-between gap-4">
              <h2
                className="text-foreground min-w-0 flex-1 truncate text-xl font-semibold"
                title={driverCompany?.name}
              >
                {driverCompany?.name}
              </h2>
              <div className="flex flex-shrink-0 items-center space-x-2">
                <Switch
                  checked={isEnabled}
                  onCheckedChange={handleToggle}
                  disabled={toggleMutation.isPending}
                />
              </div>
            </div>

            {/* Badges and Setup Button */}
            <div className="flex flex-wrap items-center gap-3">
              {hasSetup && (
                <Button
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  onClick={handleOpenSetupModal}
                  className=""
                >
                  <Icon name="Settings" className="mr-1 h-3 w-3" />
                  {setupModalTitle}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation - Only show if we have a driverGroup */}
      {!isDriverCompanyMode && (
        <div className="ordering-delivery-service-panel border-border bg-background rounded-lg border">
          <div className="border-border border-b">
            <nav className="flex space-x-4 overflow-x-auto px-6 sm:space-x-8">
              <Button
                variant="ghost"
                onClick={() => setActiveTab("general")}
                className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                  activeTab === "general"
                    ? "border-primary text-foreground"
                    : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
                }`}
              >
                {t("serviceDetail.tabs.general")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setActiveTab("businesses")}
                className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                  activeTab === "businesses"
                    ? "border-primary text-foreground"
                    : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
                }`}
              >
                {t("serviceDetail.tabs.businesses")}
              </Button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* General Tab */}
            {activeTab === "general" && (
              <div className="ordering-delivery-provider-interior space-y-6">
                {/* Configuration Settings */}
                <DriverCompanySettings pluginKey={pluginKey} />

                {/* Driver Group Assignment Settings */}
                <DriverGroupAssignmentSettings
                  driverGroup={driverGroup}
                  driverGroupId={driverGroupId}
                />
              </div>
            )}

            {/* Businesses Tab */}
            {activeTab === "businesses" && (
              <BusinessesTab
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                handleClearSearch={handleClearSearch}
                handleSelectAll={handleSelectAll}
                handleSelectNone={handleSelectNone}
                handleBusinessToggle={handleBusinessToggle}
                selectedBusinessIds={selectedBusinessIds}
                setSelectedBusinessIds={setSelectedBusinessIds}
                isLoadingBusinesses={isLoadingBusinesses}
                assignBusinessMutation={updateDriverGroupMutation}
                businesses={businesses}
                currentPage={currentPage}
                pagination={pagination}
                handlePageChange={handlePageChange}
                searchTerm={searchTerm}
                onUpdateBusinesses={handleUpdateBusinesses}
                initialSelectedBusinessIds={extractBusinessIds(
                  driverGroup?.business,
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* Configuration Settings Only - When no driverGroupId */}
      {isDriverCompanyMode && (
        <div className="ordering-delivery-service-panel border-border bg-background rounded-lg border p-6">
          <div className="ordering-delivery-provider-interior space-y-6">
            {/* Configuration Settings */}
            <DriverCompanySettings pluginKey={pluginKey} />

            {/* Warning message */}
            <div className="border-warning/30 bg-warning/20 rounded-lg border p-4">
              <div className="flex items-start space-x-3">
                <Icon
                  name="AlertCircle"
                  className="text-warning mt-0.5 h-5 w-5 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="text-warning mb-1 text-sm font-medium">
                    {t("serviceDetail.warning.title")}
                  </p>
                  <p className="text-warning/80 text-xs">
                    {t("serviceDetail.warning.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plugin Modal */}
      <PluginModal
        isOpen={isPluginModalOpen}
        onClose={() => setIsPluginModalOpen(false)}
        title={setupModalTitle}
        url={pluginSetupUrl}
      />
    </div>
  );
};

export default DeliveryServiceDetailPage;
